/* ============================================================
   JOWI — theme.js
   Cart drawer (AJAX) · Menu drawer · Predictive search · A11y
   Loaded with defer so DOM is ready when this runs.
   ============================================================ */
(function () {
  'use strict';

  /* -------------------- Helpers -------------------- */

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* formatMoney se ha eliminado: tenia el simbolo "$" escrito duro y dividia
     entre 100 a mano, lo que miente en cuanto el mercado cambia de moneda. Los
     importes del carrito los escribe ahora Liquid con el filtro `money` dentro
     de la seccion, que es lo que manda la documentacion de Shopify. */

  const trapFocus = (container) => {
    /* La lista de focusables se recalcula EN CADA Tab: el contenido del cajon
       se sustituye entero tras cada cambio del carrito y una lista capturada al
       abrirlo apunta a nodos que ya no estan en la pagina. */
    const focusables = () => Array.from(container.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => !el.disabled && el.offsetParent !== null);
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const lista = focusables();
      if (!lista.length) return;
      const first = lista[0];
      const last = lista[lista.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  /* -------------------- Drawer controller -------------------- */

  /* El nodo se busca por id CADA VEZ, no se guarda.

     Motivo: el HTML que devuelve la Section Rendering API se inserta con
     innerHTML, y ni los <script> se ejecutan ni sobreviven las referencias a
     nodos. Si alguien -este tema o una app- llega a sustituir el <aside> del
     cajon en lugar de su interior, una instancia que guardase `this.el` se
     quedaria apuntando a un nodo YA DESCONECTADO del documento: seguiria
     poniendo aria-hidden="false" sobre un nodo que nadie ve, mientras el nodo
     nuevo que si esta en la pagina conserva su aria-hidden="true" de partida.
     El cajon "se abriria" sin verse. Buscandolo por id eso no puede pasar.

     Los listeners van delegados desde `document` por lo mismo: un listener
     colgado del <aside> se pierde con el <aside>. */
  class Drawer {
    constructor(el) {
      this.id = el.id;
      this.releaseFocus = null;
      this.lastFocused = null;

      document.addEventListener('click', (e) => {
        const el = this.el;
        if (!el || !e.target || !el.contains(e.target)) return;
        if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    /* Verdad del documento, no una referencia cacheada. */
    get el() {
      return document.getElementById(this.id);
    }

    isOpen() {
      const el = this.el;
      return !!el && el.getAttribute('aria-hidden') === 'false';
    }

    open() {
      const el = this.el;
      if (!el) return;
      this.lastFocused = document.activeElement;
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = trapFocus(el);
      const firstFocus = el.querySelector('[data-drawer-initial-focus]')
        || el.querySelector('button, input, a');
      if (firstFocus) firstFocus.focus();
      el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      const el = this.el;
      if (!el) return;
      el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
      if (this.lastFocused && document.contains(this.lastFocused)) this.lastFocused.focus();
      el.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }

  const drawers = {};
  $$('[data-drawer]').forEach((el) => {
    drawers[el.id] = new Drawer(el);
  });

  $$('[data-drawer-open]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const id = trigger.getAttribute('data-drawer-open');
      if (drawers[id]) drawers[id].open();
    });
  });

  /* Upsell al abrir el cajon. Delegado desde `document`: el evento se emite
     sobre el nodo que este en la pagina en ese momento, no sobre una
     referencia guardada al arrancar. */
  document.addEventListener('drawer:open', (e) => {
    if (e.target && e.target.id === 'cart-drawer') loadCartUpsell();
  });

  /* ==========================================================================
     CARRITO — Ajax Cart API + bundled section rendering
     --------------------------------------------------------------------------
     Reconstruido siguiendo la documentacion de Shopify, no parcheando:

       https://shopify.dev/docs/api/ajax/reference/cart
       https://shopify.dev/docs/storefronts/themes/best-practices/performance/use-section-rendering-api
       https://shopify.dev/docs/storefronts/themes/pricing-payments/discounts

     Lo que impone la documentacion y que aqui se cumple:

     1. Cada mutacion (/cart/add.js, /cart/change.js, /cart/update.js) viaja con
        `sections` y `sections_url`. La respuesta trae YA el HTML repintado por
        el servidor. Una ida y vuelta, no tres (antes: mutar, luego pedir
        ?sections=, luego pedir /cart.js otra vez).

     2. Maximo CINCO secciones por peticion; mas devuelve 400 y no pinta nada.

     3. Las lineas se identifican por `item.key`, NUNCA por su numero de orden.
        El numero cambia al borrar y ademas el cajon y la pagina /cart numeraban
        igual desde 1, asi que estando en /cart un clic tocaba la linea del cajon.

     4. Los <script> del HTML devuelto NO se ejecutan al insertarlo con
        innerHTML. Por eso TODOS los listeners de aqui cuelgan de `document`,
        que nunca se reemplaza.

     5. El dinero lo formatea Liquid con el filtro `money` dentro de la seccion.
        El JS ya no escribe importes: antes tenia "$" escrito duro y dividia
        entre 100 a mano, lo que rompe en cuanto cambia la moneda del mercado.
     ========================================================================== */

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Secciones de carrito presentes en ESTA pagina, con el id real que les da
     Shopify. Ojo: el de una plantilla JSON no es la clave del JSON, lleva el
     prefijo "template--<id>__", asi que se lee del DOM y no se adivina. */
  const cartSectionIds = () =>
    $$('[data-cart-section]')
      .map((el) => el.closest('[id^="shopify-section-"]'))
      .filter(Boolean)
      .map((wrapper) => wrapper.id.replace('shopify-section-', ''))
      .filter((id, i, all) => all.indexOf(id) === i)
      .slice(0, 5);

  const Cart = {
    async get() {
      const res = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },

    /* Mutacion con secciones incluidas. `body` puede ser un objeto (JSON) o un
       FormData: los formularios de producto llevan options[], properties[] y
       items[0][...], que en JSON no se representan igual de bien. */
    async mutate(endpoint, body) {
      const secciones = cartSectionIds().join(',');
      const url = window.location.pathname;
      let init;

      if (body instanceof FormData) {
        body.append('sections', secciones);
        body.append('sections_url', url);
        init = { method: 'POST', headers: { Accept: 'application/json' }, body };
      } else {
        init = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: secciones, sections_url: url }),
        };
      }

      const res = await fetch(`${routeRoot()}cart/${endpoint}.js`, init);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw data;
      return data;
    },

    addForm(form) { return this.mutate('add', new FormData(form)); },
    addItems(items) { return this.mutate('add', { items }); },
    change(payload) { return this.mutate('change', payload); },
    update(updates) { return this.mutate('update', { updates }); },
  };

  /* El contador de la cabecera vive FUERA de cualquier seccion de carrito
     (sections/header.liquid), asi que no llega repintado y hay que ponerlo. */
  const updateCartCount = (count) => {
    if (count === null || count === undefined || Number.isNaN(count)) return;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.setAttribute('data-empty', count === 0 ? 'true' : 'false');
    });
  };

  /* Sustituye el contenido de cada seccion de carrito por el HTML del servidor.
     Se cambia el INTERIOR de [data-cart-section], no el envoltorio
     #shopify-section-*: ese envoltorio contiene el <aside> del cajon, que es
     quien guarda el estado abierto/cerrado y sus listeners. Reemplazarlo
     cerraria el cajon en cada suma. */
  const applyCartSections = (sections) => {
    if (!sections) return false;
    let aplicado = false;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return; // null = esa seccion no existe en esta pagina
      const wrapper = document.getElementById(`shopify-section-${id}`);
      if (!wrapper) return;
      const destino = wrapper.querySelector('[data-cart-section]');
      if (!destino) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresco = doc.querySelector('[data-cart-section]');
      if (!fresco) return;
      destino.innerHTML = fresco.innerHTML;
      Array.from(fresco.attributes).forEach((attr) => {
        if (attr.name.indexOf('data-cart-') === 0) destino.setAttribute(attr.name, attr.value);
      });
      aplicado = true;
    });
    return aplicado;
  };

  /* Verdad del servidor: el numero de articulos que acaba de pintar Liquid.
     /cart/add.js NO devuelve el carrito entero (solo las lineas añadidas), asi
     que leerlo de la seccion recien pintada es mas fiable que suponerlo. */
  const countFromSections = () => {
    const el = $('[data-cart-section][data-cart-item-count]');
    if (!el) return null;
    const n = parseInt(el.getAttribute('data-cart-item-count'), 10);
    return Number.isNaN(n) ? null : n;
  };

  /* Punto unico por el que pasa TODO cambio del carrito. */
  const applyCartResponse = (data) => {
    const conSecciones = applyCartSections(data && data.sections);
    const count = countFromSections();
    updateCartCount(count !== null ? count : (data && data.item_count));
    loadCartUpsell();
    applyDiscountCode();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data || {} }));
    return conSecciones;
  };

  /* ------------------------------------------------------------------------
     EL CAJON QUE "SE ESCONDE": sobrevivir a que una app recargue la pagina

     Sintoma medido en la tienda (no deducido): al pulsar "Add to cart" el cajon
     se abria a los ~800 ms y a los ~1050 ms la pagina se RECARGABA entera. El
     cajon se veia un cuarto de segundo y desaparecia.

     Quien recarga no es el tema. El tema cancela el envio nativo del formulario
     correctamente (comprobado: defaultPrevented === true) y pinta el cajon. La
     recarga la hace la app de descuentos por volumen (Avada / AOV Bundle), que
     envuelve window.fetch, ve el /cart/add, reaplica su descuento con
     /cart/update.js?app=aov-ai-bundle&skip=true y despues intenta refrescar la
     interfaz del carrito del tema. Para eso consulta su registro interno de
     temas conocidos con el nombre que tiene guardado -"Bloody Mary", que NO es
     este tema-, no encuentra ninguna seccion que pedir y cae en su ultimo
     recurso:  window.location.reload().

     La documentacion de Shopify describe exactamente este patron como el
     comportamiento por omision de las acciones de escaparate:

       «Every Liquid storefront supports actions out of the box... That default
        falls back to a full page reload when it doesn't recognize how your
        theme renders its cart.»
       https://shopify.dev/docs/api/storefront-events-and-actions/actions/configure

     Contra eso hay dos frentes, y aqui estan los dos:

     1. Decirle al escaparate COMO se abre y COMO se repinta este carrito, con
        Shopify.actions.openCart / updateCart (mas abajo). Es el contrato
        oficial: cualquier app que lo use dejara de recargar.

     2. Aguantar la recarga de quien no use ese contrato -el caso de hoy-.
        Si la pagina se va justo despues de añadir, se deja una marca fechada y
        al volver a cargar se reabre el cajon. La clienta ve su carrito abierto
        con la linea nueva y el descuento ya aplicado, en lugar de un parpadeo.

     La marca es deliberadamente de vida corta: solo vale si la pagina se fue
     dentro de la ventana siguiente a un "añadir". Si no se recarga nada, se
     borra a los pocos segundos y no reabre el cajon en la siguiente pagina que
     visite la clienta por su cuenta. */

  const RESTORE_KEY = 'jowi:cart-open';
  const RESTORE_ARM_MS = 5000;   // margen para que la app recargue
  const RESTORE_MAX_MS = 15000;  // caducidad al leer, por si quedo huerfana
  let restoreTimer = null;

  const restoreClear = () => {
    if (restoreTimer) { clearTimeout(restoreTimer); restoreTimer = null; }
    try { sessionStorage.removeItem(RESTORE_KEY); } catch (e) { /* modo privado */ }
  };

  /* Se arma al abrir el cajon tras añadir. Si nadie recarga, se desarma sola. */
  const restoreArm = () => {
    try { sessionStorage.setItem(RESTORE_KEY, String(Date.now())); } catch (e) { return; }
    if (restoreTimer) clearTimeout(restoreTimer);
    restoreTimer = setTimeout(restoreClear, RESTORE_ARM_MS);
  };

  const restorePending = () => {
    let marca = null;
    try { marca = sessionStorage.getItem(RESTORE_KEY); } catch (e) { return false; }
    if (!marca) return false;
    restoreClear();
    const t = parseInt(marca, 10);
    return !Number.isNaN(t) && Date.now() - t < RESTORE_MAX_MS;
  };

  /* Si la clienta se va por su propio pie -un enlace, "ver carrito", pagar-,
     la marca se cae: reabrir el cajon encima de la pagina que ha pedido seria
     peor que no hacer nada. Solo sobrevive la navegacion que NO ha pedido
     ella, que es justo la recarga de la app. */
  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('a[href]') || e.target.closest('[name="checkout"]')) restoreClear();
  }, true);

  /* Solo para el arranque y para recuperarse de un fallo: pide el estado real
     y repinta las secciones con la Section Rendering API.
     https://shopify.dev/docs/api/ajax/section-rendering */
  const refreshCartUI = async () => {
    const ids = cartSectionIds();
    try {
      const cart = await Cart.get();
      updateCartCount(cart.item_count);
      if (ids.length) {
        const res = await fetch(
          `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`
        );
        if (res.ok) applyCartSections(await res.json());
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    } catch (err) {
      console.error('[cart] no se ha podido leer el carrito', err);
      return null;
    }
  };

  /* Errores del carrito A LA VISTA. Antes se escribian en [data-cart-errors],
     que en la ficha no existia: la clienta pulsaba, no pasaba nada y no habia
     ni un mensaje.

     Ojo con el hueco del cajon: si el cajon esta CERRADO, escribir ahi dentro
     es lo mismo que no escribir nada. Solo valen los huecos que se ven. */
  const errorVisible = (el) => {
    const cajon = el.closest('[data-drawer]');
    if (cajon && cajon.getAttribute('aria-hidden') !== 'false') return false;
    return el.offsetParent !== null || !!cajon;
  };

  const showCartError = (mensaje, contexto) => {
    const texto = mensaje || 'Something went wrong. Please try again.';
    const local = contexto ? contexto.querySelector('[data-cart-errors]') : null;
    const objetivos = (local ? [local] : $$('[data-cart-errors]')).filter(errorVisible);
    if (objetivos.length) {
      objetivos.forEach((el) => {
        el.textContent = texto;
        el.hidden = false;
      });
      setTimeout(() => objetivos.forEach((el) => { el.hidden = true; }), 6000);
      return;
    }
    showCartToast(texto);
  };

  const clearCartErrors = () => {
    $$('[data-cart-errors]').forEach((el) => { el.hidden = true; el.textContent = ''; });
  };

  const setCartBusy = (estado) => {
    const marca = estado ? 'true' : 'false';
    const cajon = $('#cart-drawer');
    if (cajon) cajon.setAttribute('data-cart-busy', marca);
    $$('.cart-summary-section').forEach((el) => el.setAttribute('data-cart-busy', marca));
  };

  /* Un codigo de descuento NO se aplica solo. Si la tienda configura uno en el
     cajon, se manda por la ruta de Shopify para enlaces de descuento
     (/discount/CODIGO), que lo deja puesto para la pantalla de pago. Va
     APAGADO por defecto y se manda una sola vez por sesion. */
  const applyDiscountCode = () => {
    const panel = $('[data-cart-discount-code]');
    if (!panel) return;
    const codigo = panel.getAttribute('data-cart-discount-code');
    if (!codigo) return;
    let yaPuesto = null;
    try { yaPuesto = sessionStorage.getItem('jowi:discount'); } catch (e) { /* modo privado */ }
    if (yaPuesto === codigo) return;
    fetch(`${routeRoot()}discount/${encodeURIComponent(codigo)}`, { method: 'GET' })
      .then(() => { try { sessionStorage.setItem('jowi:discount', codigo); } catch (e) {} })
      .catch(() => {});
  };

  // Cart upsell — Product Recommendations API with complementary→related fallback
  const cartUpsellCache = new Map();
  let cartUpsellAbort = null;

  async function loadCartUpsell() {
    const drawer = $('#cart-drawer');
    if (!drawer) return;
    const slot = drawer.querySelector('[data-cart-upsell]');
    if (!slot) return;

    const lastItem = drawer.querySelector('[data-cart-item]:last-of-type');
    const productId = lastItem ? lastItem.getAttribute('data-product-id') : null;
    if (!productId) {
      slot.innerHTML = '';
      return;
    }

    if (cartUpsellCache.has(productId)) {
      slot.innerHTML = cartUpsellCache.get(productId);
      return;
    }

    if (cartUpsellAbort) cartUpsellAbort.abort();
    cartUpsellAbort = new AbortController();

    const fetchRecs = async (intent) => {
      const url = `${window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/'}recommendations/products?product_id=${productId}&intent=${intent}&limit=4&section_id=cart-recommendations`;
      const res = await fetch(url, { signal: cartUpsellAbort.signal });
      return res.ok ? res.text() : '';
    };

    const extractContent = (html) => {
      if (!html) return '';
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-cart-upsell-content]');
      return content ? content.outerHTML : '';
    };

    try {
      let html = await fetchRecs('complementary');
      let content = extractContent(html);
      if (!content) {
        html = await fetchRecs('related');
        content = extractContent(html);
      }
      cartUpsellCache.set(productId, content);
      slot.innerHTML = content;
    } catch (err) {
      if (err.name === 'AbortError') return;
      slot.innerHTML = '';
    }
  }

  /* --- Añadir al carrito -------------------------------------------------
     Un solo interceptor para TODOS los formularios que van a /cart/add: el de
     la ficha ({% form 'product' %}), la barra fija, la compra rapida directa de
     la rejilla y los upsells. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action$="/cart/add"]') && !form.matches('form[data-ajax-cart]')) return;
    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    /* Se guarda el innerHTML, no el textContent: los botones llevan <span> e
       iconos dentro y restaurar con textContent los borraba para siempre. */
    const etiqueta = submitBtn ? submitBtn.innerHTML : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }
    clearCartErrors();

    try {
      const data = await Cart.addForm(form);
      applyCartResponse(data);

      const cajon = $('[data-cart-section][data-cart-open-on-add]');
      const abrir = !cajon || cajon.getAttribute('data-cart-open-on-add') !== 'false';
      if (abrir && drawers['cart-drawer']) {
        drawers['cart-drawer'].open();
        /* Si una app recarga la pagina en los proximos segundos -y la de
           descuentos por volumen lo hace-, el cajon se reabre al volver. */
        restoreArm();
      }

      form.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
    } catch (err) {
      /* La Cart API devuelve {status, message, description}. `description` es
         el texto util ("No hay suficientes existencias de..."). */
      showCartError((err && (err.description || err.message)) || null, form);
      console.error('[cart] add', err);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.innerHTML = etiqueta;
      }
    }
  });

  /* --- Cambiar cantidad y borrar linea ------------------------------------
     Por `item.key`, que es unico en todo el carrito. Antes iba por numero de
     linea: el cajon y la tabla de /cart numeran IGUAL desde 1, asi que estando
     en /cart el JS encontraba primero el input del cajon (que no se
     refrescaba), la cantidad se congelaba y con 2 unidades un clic en "menos"
     vaciaba la linea entera. */
  const cambiarLinea = async (key, quantity) => {
    if (!key) return;
    setCartBusy(true);
    clearCartErrors();
    try {
      const data = await Cart.change({ id: key, quantity });
      applyCartResponse(data);
    } catch (err) {
      showCartError((err && (err.description || err.message)) || null, null);
      console.error('[cart] change', err);
      await refreshCartUI();
    } finally {
      setCartBusy(false);
    }
  };

  document.addEventListener('click', async (e) => {
    const stepper = e.target.closest('[data-cart-stepper]');
    if (stepper) {
      e.preventDefault();
      const key = stepper.getAttribute('data-cart-key');
      const delta = parseInt(stepper.getAttribute('data-delta'), 10) || 0;
      /* El input de ESTA linea, no el primero que aparezca en el documento. */
      const fila = stepper.closest('[data-cart-item]') || stepper.parentElement;
      const input = fila ? fila.querySelector('[data-cart-qty-input]') : null;
      const actual = parseInt(input ? input.value : '1', 10) || 0;
      const nueva = Math.max(0, actual + delta);
      if (input) input.value = nueva;
      await cambiarLinea(key, nueva);
      return;
    }

    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      await cambiarLinea(remove.getAttribute('data-cart-key'), 0);
    }
  });

  /* Escribir la cantidad a mano no hacia nada: no habia ningun listener. */
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const key = input.getAttribute('data-cart-key');
    if (!key) return;
    await cambiarLinea(key, Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* -------------------- Product card (Pipeline-style) -------------------- */
  // Image scrubber + swatch click + size pill click. Event delegation from document
  // so it survives AJAX re-renders of the collection grid.

  function getCardVariants(card) {
    const script = card.querySelector('[data-pcard-variants]');
    if (!script) return [];
    try {
      const cached = card._variantsCache;
      if (cached) return cached;
      const data = JSON.parse(script.textContent);
      card._variantsCache = data;
      return data;
    } catch (e) {
      return [];
    }
  }

  function getActiveColor(card) {
    return card.querySelector('[data-pcard-color-name]')?.textContent.trim() || null;
  }

  function setActiveColor(card, colorName) {
    const el = card.querySelector('[data-pcard-color-name]');
    if (el) el.textContent = colorName;
  }

  // Abbreviate labels to shortest unique prefix (used for color pills when names are long)
  //
  // El prefijo mas corto que distingue vale cuando los nombres empiezan
  // distinto. Cuando no: "Cream" era la unica variante de su ficha, no habia
  // nada de lo que distinguirla y el bucle paraba en la primera letra. Al pasar
  // el raton por QUICK ADD, la tarjeta enseñaba una pildora con una "C" suelta.
  // Igual "Dusty Pink"/"Dusty Blue" -> "D"/"D".
  //
  // Ahora: con una sola etiqueta se enseña entera, y el prefijo se alarga hasta
  // terminar la palabra que ha empezado, que es donde vuelve a significar algo.
  function abbreviateLabels(labels) {
    const result = {};
    if (labels.length <= 1) {
      labels.forEach((l) => { result[l] = l; });
      return result;
    }
    for (const label of labels) {
      let len = 1;
      let prefix = label.slice(0, len).toUpperCase();
      while (
        len < label.length &&
        labels.some(other => other !== label && other.toUpperCase().startsWith(prefix))
      ) {
        len++;
        prefix = label.slice(0, len).toUpperCase();
      }
      while (len < label.length && /\S/.test(label[len])) len++;
      result[label] = label.slice(0, len).trim();
    }
    return result;
  }

  function renderSizesForCard(card) {
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (!sizesEl) return;
    const variants = getCardVariants(card);
    if (!variants.length) return;

    const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
    const sizePos = parseInt(card.getAttribute('data-size-position'), 10);
    const activeColor = getActiveColor(card);
    const isColorMode = !sizePos && !!colorPos;

    let entries;
    if (sizePos) {
      // Build size pills filtered by active color
      const sizeIdx = sizePos - 1;
      const colorIdx = colorPos ? colorPos - 1 : -1;
      const seen = new Set();
      entries = variants
        .filter(v => colorIdx < 0 || v.opts[colorIdx] === activeColor)
        .filter(v => { if (seen.has(v.opts[sizeIdx])) return false; seen.add(v.opts[sizeIdx]); return true; })
        .map(v => ({ id: v.id, label: v.opts[sizeIdx], available: v.avail }));
    } else if (colorPos) {
      // Single-option color: pills are colors
      const colorIdx = colorPos - 1;
      const seen = new Set();
      entries = variants
        .filter(v => { if (seen.has(v.opts[colorIdx])) return false; seen.add(v.opts[colorIdx]); return true; })
        .map(v => ({ id: v.id, label: v.opts[colorIdx], available: v.avail }));
    } else {
      entries = [];
    }

    // Apply abbreviation for color mode (long names like PURPLE/BLACK don't fit)
    const labels = entries.map(e => e.label);
    const abbrs = isColorMode ? abbreviateLabels(labels) : null;

    sizesEl.innerHTML = entries.map(e => {
      // Las tallas tambien se abrevian. En el tema de origen eran codigos
      // cortos (S, M, L) y cabian; aqui son "12-24 months", que mide el triple:
      // las pastillas se iban a dos y tres lineas y el boton de compra rapida
      // CRECIA con ellas. El rotulo completo sigue en title y en aria-label.
      const display = abbrs ? abbrs[e.label] : abreviaTalla(e.label);
      const soldOutLabel = (window.themeLocales && window.themeLocales.sold_out) || 'Sold out';
      const ariaLabel = e.available ? 'Add ' + e.label + ' to Cart' : e.label + ' — ' + soldOutLabel;
      return `<li role="none"><button type="button"
        class="product-card__size-pill"
        data-pcard-size-pill
        data-variant-id="${e.id}"
        data-full-label="${e.label.replace(/"/g, '&quot;')}"
        role="menuitem"
        title="${e.label.replace(/"/g, '&quot;')}"
        ${e.available ? '' : 'disabled'}
        aria-label="${ariaLabel.replace(/"/g, '&quot;')}">
        ${display}
      </button></li>`;
    }).join('');
  }

  // "0-6 months" -> "0-6M" · "Newborn" -> "NB" · "One size" -> "OS".
  // Solo para la pastilla de la rejilla: en la ficha la talla se escribe entera.
  // Si no reconoce el patron devuelve el rotulo tal cual, que es mejor que
  // recortarlo a ciegas y enseñar una talla que no existe.
  function abreviaTalla(label) {
    const t = String(label).trim();
    if (/^newborn$/i.test(t)) return 'NB';
    if (/^one\s*size$/i.test(t) || /^talla\s*(unica|única)$/i.test(t)) return 'OS';
    const meses = t.match(/^(\d+\s*[-–]\s*\d+|\d+)\s*(months?|mo|m|meses|mes)$/i);
    if (meses) return meses[1].replace(/\s+/g, '') + 'M';
    return t;
  }

  function updateCardImageForColor(card, colorName) {
    const variants = getCardVariants(card);
    if (!variants.length) return;
    const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
    if (!colorPos) return;
    const colorIdx = colorPos - 1;
    const target = variants.find(v => v.opts[colorIdx] === colorName && v.img);
    if (!target || !target.img) return;

    const firstImg = card.querySelector('[data-pcard-img][data-image-idx="0"]');
    if (firstImg && firstImg.getAttribute('src') !== target.img) {
      firstImg.src = target.img;
      firstImg.srcset = '';
    }
  }

  // Image auto-cycle on hover (no mousemove needed)
  const cycleInterval = 900; // ms per image
  const activeCycles = new WeakMap();

  function setCardActiveImage(card, idx) {
    card.querySelectorAll('[data-pcard-img]').forEach((img) => {
      img.classList.toggle('is-active', parseInt(img.getAttribute('data-image-idx'), 10) === idx);
    });
    card.querySelectorAll('[data-bar]').forEach((bar) => {
      bar.classList.toggle('is-active', parseInt(bar.getAttribute('data-bar'), 10) === idx);
    });
  }

  function startCardCycle(card) {
    if (activeCycles.has(card)) return;
    const imagesCount = parseInt(card.getAttribute('data-images-count'), 10) || 1;
    if (imagesCount < 2) return;
    // Eager-load extra images on hover-intent
    if (!card._imagesEager) {
      card._imagesEager = true;
      card.querySelectorAll('[data-pcard-img]').forEach((img) => {
        if (img.loading === 'lazy') img.loading = 'eager';
      });
    }
    let idx = 0;
    const interval = setInterval(() => {
      if (document.hidden) return;
      idx = (idx + 1) % imagesCount;
      setCardActiveImage(card, idx);
    }, cycleInterval);
    activeCycles.set(card, interval);
  }

  function stopCardCycle(card) {
    const interval = activeCycles.get(card);
    if (interval) {
      clearInterval(interval);
      activeCycles.delete(card);
    }
    setCardActiveImage(card, 0);
  }

  document.addEventListener('mouseenter', (e) => {
    const card = e.target.closest && e.target.closest('[data-product-card]');
    if (!card) return;
    startCardCycle(card);
  }, true);

  document.addEventListener('mouseleave', (e) => {
    const card = e.target.closest && e.target.closest('[data-product-card]');
    if (!card) return;
    stopCardCycle(card);
  }, true);

  // Swatch click → swap active variant image + color name + re-render sizes
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('[data-pcard-swatch]');
    if (!swatch) return;
    e.preventDefault();
    const card = swatch.closest('[data-product-card]');
    if (!card) return;

    const colorName = swatch.getAttribute('data-color');
    const variantImg = swatch.getAttribute('data-variant-image');

    card.querySelectorAll('[data-pcard-swatch]').forEach((s) => {
      const isThis = s === swatch;
      s.classList.toggle('is-active', isThis);
      s.setAttribute('aria-pressed', isThis ? 'true' : 'false');
    });

    setActiveColor(card, colorName);

    if (variantImg) {
      const firstImg = card.querySelector('[data-pcard-img][data-image-idx="0"]');
      if (firstImg) {
        firstImg.src = variantImg;
        firstImg.srcset = '';
      }
    } else {
      updateCardImageForColor(card, colorName);
    }

    // Update PDP link href if active variant has matching variant ID
    const variants = getCardVariants(card);
    if (variants.length) {
      const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
      if (colorPos) {
        const first = variants.find(v => v.opts[colorPos - 1] === colorName && v.avail) || variants.find(v => v.opts[colorPos - 1] === colorName);
        if (first) {
          const productUrl = card.getAttribute('data-product-url');
          card.querySelectorAll('a[href]').forEach((a) => {
            if (a.href.includes(productUrl)) {
              a.href = `${productUrl}?variant=${first.id}`;
            }
          });
        }
      }
    }
  });

  // Hover/focus on quick-add: build size pills if not yet built
  document.addEventListener('mouseenter', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-pcard-quick-add-trigger]');
    if (!trigger) return;
    const card = trigger.closest('[data-product-card]');
    if (!card) return;
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (sizesEl && !sizesEl.children.length) {
      renderSizesForCard(card);
    }
  }, true);

  document.addEventListener('focusin', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-pcard-quick-add-trigger]');
    if (!trigger) return;
    const card = trigger.closest('[data-product-card]');
    if (!card) return;
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (sizesEl && !sizesEl.children.length) {
      renderSizesForCard(card);
    }
    const qa = card.querySelector('[data-pcard-quick-add]');
    if (qa) qa.setAttribute('data-state', 'sizes-open');
  });

  // Cart notification banner (toast-style, no drawer auto-open)
  let cartNotifTimeout = null;
  let lastAddedPill = null;
  let lastAddedPillReset = null;

  function ensureCartNotification() {
    let banner = document.querySelector('[data-cart-notification]');
    if (banner) return banner;
    banner = document.createElement('div');
    banner.setAttribute('data-cart-notification', '');
    banner.className = 'cart-notification';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    const addedLabel = window.themeLocales && window.themeLocales.cart_added || 'ADDED TO CART';
    const viewLabel = window.themeLocales && window.themeLocales.view_cart || 'VIEW CART';
    banner.dataset.addedLabel = addedLabel;
    banner.innerHTML = `
      <div class="cart-notification__inner">
        <span class="cart-notification__text" data-cart-notification-text></span>
        <button type="button" class="cart-notification__view" data-cart-notification-view>${viewLabel}</button>
      </div>
    `;
    document.body.appendChild(banner);

    banner.querySelector('[data-cart-notification-view]').addEventListener('click', () => {
      if (drawers['cart-drawer']) drawers['cart-drawer'].open();
      banner.classList.remove('is-active');
    });
    return banner;
  }

  /* Aviso flotante con un texto tal cual, sin añadirle "ADDED TO CART".
     showCartNotification lo pega siempre, y un error leido como
     "Sin stock  ADDED TO CART" es peor que no decir nada. */
  function showCartToast(mensaje) {
    const banner = ensureCartNotification();
    const text = banner.querySelector('[data-cart-notification-text]');
    if (text) text.textContent = mensaje;
    banner.classList.add('is-active');
    clearTimeout(cartNotifTimeout);
    cartNotifTimeout = setTimeout(() => banner.classList.remove('is-active'), 5000);
  }

  function showCartNotification(productTitle, sizeLabel, sourcePill) {
    const banner = ensureCartNotification();
    const text = banner.querySelector('[data-cart-notification-text]');
    const sizePart = sizeLabel ? ` ${sizeLabel}` : '';
    text.textContent = `${productTitle}${sizePart}  ${banner.dataset.addedLabel}`;

    // Reset previously marked pill
    if (lastAddedPillReset) {
      lastAddedPillReset();
      lastAddedPillReset = null;
    }

    if (sourcePill) {
      const originalHTML = sourcePill.innerHTML;
      sourcePill.classList.add('is-added');
      sourcePill.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
      sourcePill.disabled = true;
      lastAddedPill = sourcePill;
      lastAddedPillReset = () => {
        sourcePill.classList.remove('is-added');
        sourcePill.innerHTML = originalHTML;
        sourcePill.disabled = false;
      };
    }

    banner.classList.add('is-active');
    clearTimeout(cartNotifTimeout);
    cartNotifTimeout = setTimeout(() => {
      banner.classList.remove('is-active');
      if (lastAddedPillReset) {
        lastAddedPillReset();
        lastAddedPillReset = null;
      }
    }, 4000);
  }

  // Size pill click → AJAX add to cart + show banner notification
  document.addEventListener('click', async (e) => {
    const pill = e.target.closest('[data-pcard-size-pill]');
    if (!pill) return;
    if (pill.disabled) return;
    e.preventDefault();
    const variantId = pill.getAttribute('data-variant-id');
    if (!variantId) return;
    pill.setAttribute('aria-busy', 'true');

    const card = pill.closest('[data-product-card]');
    const productTitle = card ? (card.querySelector('.product-card__title a')?.textContent.trim() || '') : '';
    const sizeLabel = pill.getAttribute('data-full-label') || pill.textContent.trim();

    try {
      const data = await Cart.addItems([{ id: variantId, quantity: 1 }]);
      applyCartResponse(data);
      showCartNotification(productTitle, sizeLabel, pill);
    } catch (err) {
      /* Antes este fallo solo iba a la consola: la pastilla se quedaba
         "pensando" y la clienta no sabia por que no pasaba nada. */
      showCartError((err && (err.description || err.message)) || null, null);
      console.error('[card quick-add]', err);
    } finally {
      pill.removeAttribute('aria-busy');
    }
  });

  // Listen also for cart:item-added events (e.g. from main product form) → show banner
  document.addEventListener('cart:item-added', (e) => {
    const form = e.target;
    if (!form || !form.matches) return;
    // Skip the cart drawer's own quantity changes etc.
    if (form.closest('[data-cart-items]')) return;
    if (form.matches('[data-product-card] form')) return; // already handled above (it's a direct submit form on card)
  });

  /* -------------------- Predictive search -------------------- */

  const searchModal = $('#search-modal');
  if (searchModal) {
    const input = $('[data-search-input]', searchModal);
    const resultsEl = $('[data-search-results]', searchModal);
    let abortController;
    let debounceTimer;

    const runSearch = async (term) => {
      if (!term || term.length < 2) {
        if (resultsEl) resultsEl.innerHTML = '';
        return;
      }
      if (abortController) abortController.abort();
      abortController = new AbortController();
      try {
        const url = `/search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: abortController.signal });
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        if (section && resultsEl) resultsEl.innerHTML = section.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[search]', err);
      }
    };

    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => runSearch(input.value.trim()), 200);
      });
    }
  }

  /* -------------------- Sticky header on scroll -------------------- */

  const header = $('[data-header-sticky]');
  if (header) {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      header.classList.toggle('is-hidden', y > lastY && y > 120);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Announcement bar rotation: moved to {% javascript %} of the section
     for better scope isolation. See sections/announcement-bar.liquid */

  /* -------------------- Discount code copy -------------------- */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy-code]');
    if (!btn) return;
    const code = btn.getAttribute('data-copy-code');
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      const original = btn.textContent;
      btn.textContent = (window.themeLocales && window.themeLocales.copied) || 'Copied!';
      btn.setAttribute('aria-live', 'polite');
      setTimeout(() => { btn.textContent = original; }, 1800);
    });
  });

  /* -------------------- Init -------------------- */

  /* Al arrancar solo hace falta cuadrar el contador de la cabecera: las
     secciones de carrito ya vienen pintadas por Liquid en esta misma carga.
     Antes se pedia /cart.js en TODAS las paginas sin necesidad. */
  updateCartCount(countFromSections());
  applyDiscountCode();

  /* Cerrar el cajon a mano cancela la reapertura: si la clienta lo cierra y una
     app recarga despues, volver a abrirlo seria pelearse con ella. */
  document.addEventListener('drawer:close', (e) => {
    if (e.target && e.target.id === 'cart-drawer') restoreClear();
  });

  /* Reapertura tras una recarga ajena (ver el bloque RESTORE_KEY). En /cart no
     se hace: ahi el carrito ya es la pagina, y taparla con el cajon sobra. */
  if (restorePending()
      && drawers['cart-drawer']
      && document.body.className.indexOf('template-cart') === -1) {
    drawers['cart-drawer'].open();
  }

  /* ------------------------------------------------------------------------
     CONTRATO OFICIAL CON LAS APPS — Shopify.actions

     https://shopify.dev/docs/api/storefront-events-and-actions/actions/configure
     https://shopify.dev/docs/api/storefront-events-and-actions/actions/open-cart

     Sin configurar, la accion por omision «falls back to a full page reload
     when it doesn't recognize how your theme renders its cart». Este tema no
     se parece a Dawn, asi que ninguna app lo reconoce: de ahi la recarga.
     Aqui se le dice al escaparate como se abre este cajon y como se repinta,
     para que `openCart()` y `updateCart()` de cualquier app hagan lo correcto
     en lugar de recargar.

     Aviso de la documentacion: «Only the first configure call on an action
     takes effect», y recomienda registrarlo por encima de {{ content_for_header }}.
     theme.js va con `defer` al final del <body>, asi que no se puede garantizar
     ser el primero; se comprueba el valor devuelto y se deja constancia. Mover
     esto al layout haria el contrato inmune al orden -queda anotado en el
     informe, layout/theme.liquid no esta en el alcance de este trabajo-. */
  try {
    const acciones = window.Shopify && window.Shopify.actions;

    if (acciones && acciones.openCart && typeof acciones.openCart.configure === 'function') {
      const ok = acciones.openCart.configure({
        handler() {
          if (drawers['cart-drawer']) {
            drawers['cart-drawer'].open();
            return;
          }
          window.location.href = routeRoot() + 'cart';
        },
      });
      if (ok === false) console.warn('[cart] openCart ya lo habia configurado otro script');
    }

    if (acciones && acciones.updateCart && typeof acciones.updateCart.configure === 'function') {
      const ok = acciones.updateCart.configure({
        /* Los eventos que emite la accion salen del cajon, para que quien
           escuche acotado a esa parte de la pagina los reciba. */
        eventTarget: () => document.getElementById('cart-drawer') || document,
        /* Se deja que Shopify escriba en el carrito y luego se repinta con la
           Section Rendering API, que es lo que esta app no sabe hacer sola. */
        async handler(defaultHandler) {
          const resultado = await defaultHandler();
          await refreshCartUI();
          return resultado;
        },
      });
      if (ok === false) console.warn('[cart] updateCart ya lo habia configurado otro script');
    }
  } catch (err) {
    console.warn('[cart] Shopify.actions no disponible', err);
  }

  // Expose for sections / debugging
  window.ShopTheme = { Cart, drawers, refreshCartUI, applyCartResponse };
})();
